import { useCallback, useEffect, useState } from 'react'
import { useTableSelection } from './use-table-selection'

interface TableSelectionStorage {
  selectedTableIds: string[]
  timestamp: number
}

interface UsePersistentTableSelectionOptions<T> {
  items: T[]
  getId: (item: T) => string
  onSelectionChange: (selectedIds: string[]) => void
  schemaName: string
  storageKey?: string
}

/**
 * Custom hook to manage table selection persistence in localStorage
 */
export const usePersistentTableSelection = <T>({
  items,
  getId,
  onSelectionChange,
  schemaName,
  storageKey,
}: UsePersistentTableSelectionOptions<T>) => {
  const getStorageKey = useCallback(
    (schema: string) => {
      return storageKey || `table_selection_${schema}`
    },
    [storageKey],
  )

  const [selectedTableIds, setSelectedTableIds] = useState<string[]>(() => {
    if (!schemaName) return []

    try {
      const key = getStorageKey(schemaName)
      const stored = localStorage.getItem(key)

      if (stored) {
        const parsed: TableSelectionStorage = JSON.parse(stored)
        const isRecent = Date.now() - parsed.timestamp < 7 * 24 * 60 * 60 * 1000

        if (isRecent && Array.isArray(parsed.selectedTableIds)) {
          return parsed.selectedTableIds
        }
      }
    } catch (error) {
      console.error('Failed to load table selections from storage:', error)
    }

    return []
  })

  const saveSelectionsToStorage = useCallback(
    (selections: string[]) => {
      if (!schemaName) return

      try {
        const key = getStorageKey(schemaName)
        const storageData: TableSelectionStorage = {
          selectedTableIds: selections,
          timestamp: Date.now(),
        }

        localStorage.setItem(key, JSON.stringify(storageData))
      } catch (error) {
        console.error('Failed to save table selections to storage:', error)
      }
    },
    [schemaName, getStorageKey],
  )

  const clearOtherSchemaSelections = useCallback(() => {
    try {
      const keysToRemove: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith('table_selection_') && key !== getStorageKey(schemaName)) {
          keysToRemove.push(key)
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key))
    } catch (error) {
      console.error('Failed to clear other schema selections:', error)
    }
  }, [schemaName, getStorageKey])

  // Initialize selections when schema changes
  useEffect(() => {
    if (!schemaName || items.length === 0) {
      return
    }

    clearOtherSchemaSelections()

    if (selectedTableIds.length === 0) {
      const allIds = items.map(getId)
      setSelectedTableIds(allIds)
      onSelectionChange(allIds)
      saveSelectionsToStorage(allIds)
    } else {
      onSelectionChange(selectedTableIds)
    }
  }, [schemaName, items.length])

  // Auto-save to localStorage
  useEffect(() => {
    if (selectedTableIds.length > 0) {
      saveSelectionsToStorage(selectedTableIds)
    }
  }, [selectedTableIds, saveSelectionsToStorage])

  const updateSelections = useCallback(
    (newSelections: string[]) => {
      const validSelections = newSelections.filter((id) => items.some((item) => getId(item) === id))
      setSelectedTableIds(validSelections)
      onSelectionChange(validSelections)
    },
    [items, getId, onSelectionChange],
  )

  // Use the base table selection hook for UI logic
  const tableSelection = useTableSelection({
    items,
    selectedIds: selectedTableIds,
    getId,
    onSelectionChange: updateSelections,
  })

  return {
    ...tableSelection,
    selectedIds: selectedTableIds,
    updateSelections,
    areAllTablesSelected: selectedTableIds.length === items.length && items.length > 0,
    isTableSelected: (tableId: string) => selectedTableIds.includes(tableId),
  }
}
